import { useApi } from "../../lib/useApi.ts";
import { Loading, ScreenHead, Tile } from "./ConsoleLayout.tsx";

interface OverviewData {
  installs_today: number; active_7d: number; active_30d: number; silent: number;
  update_success_pct: number | null; open_crash_groups: number; unread_feedback: number;
  version_spread: { release: string | null; devices: number }[];
  rolling: { version: string; channel: string; rollout_pct: number; status: string; baseline_fail: number }[];
}

// Categorical slots in fixed order; a fifth version folds into "Other".
const SERIES = ["#3fd8f0", "#8b97ff", "#ff8a9a", "#ffc95c"];

export function Overview() {
  const { data, error } = useApi<OverviewData>("/api/console/overview");
  if (!data) return <><ScreenHead label="Overview" /><Loading error={error} /></>;

  const top = data.version_spread.slice(0, 3);
  const rest = data.version_spread.slice(3).reduce((a, b) => a + b.devices, 0);
  const spread = [...top.map((v) => ({ label: v.release ?? "unknown", n: v.devices })), ...(rest ? [{ label: "Other", n: rest }] : [])];
  const total = spread.reduce((a, b) => a + b.n, 0);

  return (
    <div className="flex flex-col gap-6">
      <ScreenHead label="Overview" />
      {data.rolling.map((r) => (
        <div key={r.version} className={`rounded-[14px] px-4 py-3 text-[15px] text-ink-2 ${r.status === "paused" ? "bg-warn/10 shadow-[inset_0_0_0_1px_rgb(255_201_92/.4)]" : "bg-good/10 shadow-[inset_0_0_0_1px_rgb(94_224_173/.35)]"}`}>
          <b className="text-ink">{r.version}</b> ({r.channel}) {r.status === "paused" ? "is paused" : `is rolling at ${r.rollout_pct}%`}. Halt line {(r.baseline_fail * 2).toFixed(1)}% apply failure.
        </div>
      ))}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <Tile label="Installs today" value={data.installs_today} note="first boots" />
        <Tile label="Active, 7 days" value={data.active_7d} />
        <Tile label="Active, 30 days" value={data.active_30d} note={`${data.silent} silent`} />
        <Tile label="Update success" value={data.update_success_pct === null ? "–" : `${data.update_success_pct}%`} note="last 30 days" />
        <Tile label="Open crash groups" value={data.open_crash_groups} alert={data.open_crash_groups > 0} />
        <Tile label="Unread feedback" value={data.unread_feedback} />
      </div>
      <section className="card p-5">
        <h2 className="mb-3 text-[17px]">Version spread across active devices, last 7 days</h2>
        {total === 0 ? <p className="text-[15px] text-ink-3">No active devices yet.</p> : (
          <>
            <div className="flex h-6 gap-0.5" role="img" aria-label={spread.map((s) => `${s.label} ${s.n}`).join(", ")}>
              {spread.map((s, i) => (
                <span key={s.label} title={`${s.label}: ${s.n} devices`} style={{ width: `${(s.n / total) * 100}%`, background: SERIES[i] }}
                  className="h-full first:rounded-l last:rounded-r" />
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-4 text-[13px] text-ink-2 tabular-nums">
              {spread.map((s, i) => (
                <span key={s.label} className="flex items-center gap-1.5">
                  <i className="inline-block size-2.5 rounded-sm" style={{ background: SERIES[i] }} />{s.label} {Math.round((s.n / total) * 100)}%
                </span>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
